import React from "react";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import { CloseIcon, ProceedArrow } from "asset/icons";
import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import { useMutation, useQueryClient } from "react-query";
import { handleChangeShipmentStatus } from "api/fetchers";
import Loader from "component/loader";
import { shipmentStatusList, shippingType } from "constants/global";
import { toast } from "react-toastify";
import { toNumber } from "lodash";

function toOrdinal(n) {
    const num = toNumber(n);
    const suffixes = ["th", "st", "nd", "rd"];
    const v = num % 100;
    return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}

export default function UpdateStatusModal({
    show,
    onHide,
    shipmentDetails,
    shipmentId,
    refetchData,
}) {
    const [remarks, setRemarks] = useState("");
    const [date, setDate] = useState("");
    const [status, setStatus] = useState("");
    const [legDetails, setLegDetails] = useState();
    const queryClient = useQueryClient();

    const closeModal = () => {
        onHide();
        setRemarks("");
        setDate("");
        setStatus("");
        setLegDetails(undefined);
    };

    const { mutateAsync, isLoading } = useMutation({
        mutationFn: (data) => handleChangeShipmentStatus(data, shipmentId),
        mutationKey: ["changeShipmentStatus", legDetails?._id],
        onSuccess: (data) => {
            if (!data?.message.includes("successfully")) {
                toast.error(
                    data?.response?.data?.message || data?.message || "Something went wrong"
                );
            } else {
                toast.success(data?.message);
            }
            queryClient.invalidateQueries({ queryKey: ["single-shipment", shipmentId] });
            refetchData?.();
            closeModal();
        },
    });

    const dropDownOption = shipmentStatusList
        ?.filter((item) => {
            return !legDetails?.status?.some((stat) => stat.status === item.value);
        })
        ?.map((option) => ({
            label: option.label,
            value: option.value,
        }));
    const legTypeOptions =
        shipmentDetails?.map((option, index) => {
            return {
                label: `${toOrdinal(index + 1)} Leg: ${option?.type}`,
                value: index,
            };
        }) || [];

    const placeHolder = `1st leg: ${shippingType?.[shipmentDetails?.[0]?.type]}`;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            contentClassName="!rounded-xl w-full border-0 max-w-[453px] mx-auto"
        >
            <div className="relative">
                <div className="flex items-center justify-between p-4">
                    <div>
                        <h3 className="mb-0 text-2xl font-semibold text-main-dark">
                            Update status
                        </h3>
                        <p className="mt-1 text-sm font-light text-gray-50">
                            Set the current status of the shipment
                        </p>
                    </div>
                    <CloseIcon
                        onClick={() => {
                            closeModal();
                        }}
                        className="w-6 h-6 text-gray-500 cursor-pointer hover:text-gray-800"
                    />
                </div>

                {/* Legs Selection */}
                <div className="px-4">
                    {/* 1st Leg */}
                    <div className="">
                        <Dropdown
                            value={legDetails?.label || ""}
                            name="loadType"
                            options={legTypeOptions}
                            onChange={(value) => {
                                setLegDetails({
                                    ...shipmentDetails[value],
                                    label: `${toOrdinal(value + 1)} Leg: ${
                                        shippingType?.[shipmentDetails?.[value]?.type]
                                    }`,
                                });
                            }}
                            label="Which leg are you updating?"
                            labelClassName="!text-[#475467]"
                            placeholder={placeHolder}
                        />
                    </div>
                    {legDetails ? (
                        <div className="">
                            <div className="mt-3 mb-4 border rounded-md border-light-grey">
                                <div
                                    style={{ borderBottom: "1px solid #dee2e6" }}
                                    className="flex items-center justify-between w-full gap-2 px-2 py-3 text-sm font-medium text-gray-10 gap-x-2"
                                >
                                    <p
                                        title={
                                            legDetails?.departurePort?.address
                                                ? legDetails?.departurePort?.address
                                                : legDetails?.departurePort
                                        }
                                        className="w-6/12 overflow-hidden text-left text-ellipsis whitespace-nowrap"
                                    >
                                        {legDetails?.departurePort?.address
                                            ? legDetails?.departurePort?.address
                                            : legDetails?.departurePort}
                                    </p>
                                    <ProceedArrow />

                                    <p
                                        title={
                                            legDetails?.destinationPort?.address
                                                ? legDetails?.destinationPort?.address
                                                : legDetails?.destinationPort
                                        }
                                        className="w-6/12 overflow-hidden text-right text-ellipsis whitespace-nowrap"
                                    >
                                        {legDetails?.destinationPort?.address
                                            ? legDetails?.destinationPort?.address
                                            : legDetails?.destinationPort}
                                    </p>
                                </div>
                                <div className="p-2">
                                    <Dropdown
                                        value={
                                            shipmentStatusList?.find(
                                                (stat) => stat?.value === status
                                            )?.label
                                        }
                                        name="loadType"
                                        options={dropDownOption}
                                        onChange={(value) => setStatus(value)}
                                        label="Select status"
                                        labelClassName="!text-[#475467] mt-2"
                                        placeholder="Picked Up"
                                    />

                                    <div className="">
                                        <Input
                                            type="date"
                                            label="Date"
                                            placeholder="01-03-2024"
                                            name="dropOffDate"
                                            value={date}
                                            onChange={(e) => setDate(e)}
                                        />
                                    </div>

                                    <Input
                                        label="Remarks"
                                        placeholder="Enter Remarks"
                                        name="remarks"
                                        max={64}
                                        value={remarks}
                                        onChange={(e) => {
                                            if (e.length > 64) {
                                                return;
                                            } else {
                                                setRemarks(e);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-3 mt-8 space-x-3 border-t border-gray-200">
                    <button className="px-5 py-2 text-gray-700 rounded-md" onClick={closeModal}>
                        Cancel
                    </button>
                    <button
                        className="px-4 py-2 text-white rounded-md !bg-secondary"
                        disabled={isLoading || !legDetails}
                        onClick={() => {
                            mutateAsync({
                                status,
                                id: legDetails?._id,
                                remark: remarks,
                                date,
                            });
                        }}
                    >
                        {isLoading ? <Loader /> : "Update Status"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
